import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { authService, accountService, ApiError, AccountDto } from '../services';

type User = AccountDto;

export interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  verifyToken: (token: string, landingUrl?: string, additionalParams?: Record<string, string>) => Promise<{ success: boolean; message: string; landingUrl?: string; additionalParams?: Record<string, string> }>;
  setAuthenticatedUser: (token: string, account: AccountDto) => void;
  authApi: <T>(request: () => Promise<T>) => Promise<T>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && !!token;

  // Helper function for dynamic language loading
  const setUserLanguage = async (language: string) => {
    try {
      const { setLanguage } = await import('../i18n/setLanguage');
      setLanguage(language);
    } catch (error) {
      console.error('Error loading language module:', error);
    }
  };

  useEffect(() => {
    const validateToken = async () => {
      const savedToken = localStorage.getItem('authToken');
      if (savedToken) {
        try {
          // Verify token with the backend
          const profile = await accountService.getProfile(savedToken);
          const savedUser = localStorage.getItem('authUser');

          if (savedUser) {
            setUser(JSON.parse(savedUser));
          } else {
            // If user data is missing, use profile data and save it
            setUser(profile);
            localStorage.setItem('authUser', JSON.stringify(profile));
          }
          setToken(savedToken);
          if (profile.preferredLanguage) {
            setUserLanguage(profile.preferredLanguage);
          }
        } catch (error) {
          if (error instanceof ApiError && error.statusCode === 401) {
            // Token is invalid/expired, clear session
            logout();
          }
          // For other errors, we can just log them and proceed to a logged-out state
          console.error('Token validation failed:', error);
        }
      }
      setIsLoading(false);
    };

    validateToken();
  }, []);

  const login = async (email: string, recaptchaToken?: string): Promise<{ success: boolean; message: string }> => {
    try {
      const data = await authService.login(email, recaptchaToken);

      // If we get here with a 200 response, login was successful
      const successMessage = (data.message === 'MAGIC_LINK_SENT' || data.message === 'ACCOUNT_EXISTS_MAGIC_LINK_SENT')
        ? t('auth.magicLinkSent')
        : data.message || t('auth.magicLinkSent');
      return { success: true, message: successMessage };
    } catch (error) {
      if (error instanceof ApiError) {
        let errorMessage;
        switch (error.message) {
          case 'INVALID_CREDENTIALS':
            errorMessage = t('auth.invalidCredentials');
            break;
          case 'EMAIL_NOT_CONFIRMED':
            errorMessage = t('auth.emailNotConfirmed');
            break;
          case 'LOGIN_ERROR':
            errorMessage = t('auth.loginError');
            break;
          default:
            errorMessage = error.message;
        }
        return { success: false, message: errorMessage };
      }
      console.error('Login error:', error);
      return { success: false, message: t('auth.loginError') };
    }
  };

  const verifyToken = async (verificationToken: string, landingUrl?: string, additionalParams?: Record<string, string>): Promise<{ success: boolean; message: string; landingUrl?: string; additionalParams?: Record<string, string> }> => {
    try {
      const data = await authService.verifyToken(verificationToken, landingUrl, additionalParams);

      // Backend returns { token, account, expiresAt, landingUrl?, additionalParams? } on success
      if (data.token && data.account) {
        setToken(data.token);
        setUser(data.account);
        
        // Set user's preferred language
        if (data.account && data.account.preferredLanguage) {
          setUserLanguage(data.account.preferredLanguage);
        }
        
        // Store in localStorage
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('authUser', JSON.stringify(data.account));
        
        return { 
          success: true, 
          message: t('auth.loginSuccess'),
          landingUrl: data.landingUrl,
          additionalParams: data.additionalParams
        };
      } else {
        // This should not happen with a 200 response, but keep as fallback
        return { success: false, message: t('auth.tokenExpired') };
      }
    } catch (error) {
      if (error instanceof ApiError) {
        // Handle error codes from backend
        let errorMessage;
        switch (error.message) {
          case 'INVALID_CREDENTIALS':
            errorMessage = t('auth.invalidCredentials');
            break;
          case 'INVALID_TOKEN':
            errorMessage = t('auth.invalidToken');
            break;
          case 'TOKEN_ALREADY_USED':
            errorMessage = t('auth.tokenAlreadyUsed');
            break;
          case 'TOKEN_EXPIRED':
            errorMessage = t('auth.tokenExpiredError');
            break;
          case 'EMAIL_NOT_CONFIRMED':
            errorMessage = t('auth.emailNotConfirmed');
            break;
          case 'LOGIN_ERROR':
            errorMessage = t('auth.loginError');
            break;
          default:
            errorMessage = error.message;
        }
        return { success: false, message: errorMessage };
      }
      console.error('Token verification error:', error);
      return { success: false, message: t('auth.loginError') };
    }
  };

  const setAuthenticatedUser = (authToken: string, account: AccountDto) => {
    setToken(authToken);
    setUser(account);
    
    // Set user's preferred language
    if (account && account.preferredLanguage) {
      setUserLanguage(account.preferredLanguage);
    }
    
    // Store in localStorage
    localStorage.setItem('authToken', authToken);
    localStorage.setItem('authUser', JSON.stringify(account));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    navigate('/', { replace: true });
  };

  const authApi = async <T,>(request: () => Promise<T>): Promise<T> => {
    try {
      return await request();
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 401) {
        // Specific error codes that indicate an invalid/expired session
        const sessionErrorCodes = ['TOKEN_EXPIRED', 'INVALID_TOKEN'];
        if (error.errorCode && sessionErrorCodes.includes(error.errorCode)) {
          logout();
        }
      }
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
    verifyToken,
    setAuthenticatedUser,
    authApi,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};